'use client';

import { normalizeString } from "@/functions/normalize_string";
import { getProvidersByFilter } from "@/utils/http";
import { useMutation } from "@tanstack/react-query";
import { useSearchParams } from "next/navigation";
import { createContext, useContext, useEffect, useState } from "react";

const FilterContext = createContext({
    isFavorit: false,
    setIsFavorit: () => {},
    name: "",
    setName: () => {},
    addressCity: "",
    setAddressCity: () => {},
    distance: 0,
    setDistance: () => {},
    others: {},
    setOthers: () => {},
    providers: {},
    perPage: "",
    setPerPage: () => {},
    order: "",
    setOrder: () => {},
    pager: {},
    setPager: () => {},
    showMore: () => {},
    removeFilter: () => {},
    getFilterBadges: () => {}
});

const initOthers = (others, searchParams) => {
    if(!others || others.length === 0) return [];

    let to_return = []
    for(let o in others) {
        to_return.push({...others[o], value: ((searchParams && searchParams[others[o].filter_name]) ? searchParams[others[o].filter_name] : "")});
    }

    return to_return;
}

export function FilterContextProvider({searchParams, ...props}) {
    const search_params = useSearchParams();
    const [isPopState, setIsPopState] = useState(false);

    const [isFavorit, setIsFavorit] = useState((searchParams && searchParams.is_favourite) ? +searchParams.is_favourite : false);
    const [name, setName] = useState((searchParams && searchParams.name) ? searchParams.name.substring(1, searchParams.name.length-1) : "");
    const [addressCity, setAddressCity] = useState(props.addressCity ? props.addressCity : "");
    const [distance, setDistance] = useState((searchParams && searchParams.distance) ? searchParams.distance : 0);
    const [others, setOthers] = useState(initOthers(props.others, searchParams));
    const [providers, setProviders] = useState(props.providers ? props.providers : {});
    const [perPage, setPerPage] = useState((searchParams && searchParams.per_page) ? Number(searchParams.per_page) : 12);
    const [order, setOrder] = useState((searchParams && searchParams.order) ? searchParams.order : "");
    const [pager, setPager] = useState(props.pager ? props.pager : {})

    useEffect(() => {
        if (isPopState) {
            setIsFavorit(search_params.get("is_favourite") ? true : false)
            setName(search_params.get("name") ? search_params.get("name").substring(1, search_params.get("name").length-1) : "");
            setDistance(search_params.get("distance") ? search_params.get("distance") : 0);
            setPerPage(search_params.get("per_page") ? Number(search_params.get("per_page")) : 12);
            setOrder(search_params.get("order") ? search_params.get("order") : "");

            setOthers(prev => {
                let temp_prev = [...prev];
                if(temp_prev.length > 0) {
                    for(let t in temp_prev) {
                        temp_prev[t].value = (search_params.get(temp_prev[t].filter_name) ? search_params.get(temp_prev[t].filter_name) : "")
                    }
                }
                return temp_prev;
            })

            setIsPopState(false);
        }
    }, [isPopState, search_params]);

    useEffect(() => {
        const handlePopState = () => {
            setIsPopState(true);
        };
    
        window.addEventListener('popstate', handlePopState);
    
        return () => {
          window.removeEventListener('popstate', handlePopState);
        };
    }, []);
    
    const { mutate: getMore } = useMutation({
        mutationFn: getProvidersByFilter,
        onSuccess: (response, params) => {
            if(response.success) {
                setProviders(prev => {
                    return {
                        ...prev,
                        providers: [...prev.providers, ...response.response.providers]
                    }
                })
                setPager(response.pager);
            }
        }
    });

    const showMore = (provider_type, city) => {
        let temp_params = {};
        if(city) {
            if(city.startsWith("budapest-")) temp_params["filter[address_district]"] = city.replace("budapest-","");
            if(city.endsWith("-megye")) temp_params["filter[address_county]"] = city.replace("-megye","");
            if(city.endsWith("-regio")) temp_params["filter[address_region]"] = city.replace("-regio","");
            else temp_params["filter[address_city]"] = city;
        }
        if(name) temp_params["filter[name]"] = name;
        if(+distance) temp_params["filter[distance]"] = distance;
        if(isFavorit) temp_params["filter[is_favourite]"] = isFavorit;
        temp_params["per_page"] = (perPage ? perPage : 25);
        if(order) temp_params["order"] = order;
    
        if(providers.nearby_cities && city) {
            let temp_city = city;
            let filter_param = "address_city";
            
            if(city.endsWith("-megye")) {
                temp_city = city.replace("-megye","")
                filter_param = "address_county";
            }
            else if(city.endsWith("-regio")) {
                temp_city = city.replace("-regio","")
                filter_param = "address_region";
            }
            else if(city.startsWith("budapest-")) {
                city = city.replace("budapest-","")
                filter_param = "address_district";
            }

            for(let n in providers.nearby_cities) temp_params[`filter[${filter_param}]`] += `,${normalizeString(providers.nearby_cities[n].city)}`;
        }
    

        getMore({pager: true, params: {page: pager.page+1, "filter[type_id]": provider_type, ...temp_params}})
    }

    const removeOtherFilter = (field) => {
        setOthers(prev => {
            let temp_prev = [...prev];
            let temp_prev_index = temp_prev.findIndex(t => t.filter_name === field);
            if(temp_prev_index === -1) return prev;

            temp_prev[temp_prev_index].value = "";
            return temp_prev;
        })
    }

    const removeFilter = (field) => {
        switch(field) {
            case "is_favourite": setIsFavorit(false); break;
            case "name": setName(""); break;
            case "distance": setDistance(0); break;
            default: removeOtherFilter(field);
        }
    }

    const getOtherFilterName = (field, value) => {
        let option_index = others.findIndex(o => o.filter_name === field);
        if(option_index === -1) return "-";

        let field_index = others[option_index].fields.findIndex(f => f.filter_name === value);
        if(field_index === -1) return "-";

        return others[option_index].fields[field_index].name;
    }

    const removeName = () => {
        setName("");
        return "delete";
    }

    const removeFavourite = () => {
        setIsFavorit(false);
        return "delete";
    }

    const removeDistance = () => {
        setDistance(0);
        return "delete";
    }

    const removeOtherRadio = (key) => {
        setOthers(prev => {
            let temp_prev = [...prev];
            let temp_prev_index = temp_prev.findIndex(t => t.filter_name === key);
            if(temp_prev_index === -1) return prev;

            temp_prev[temp_prev_index].value = "";
            return temp_prev;
        })
        return "delete";
    }

    const removeOtherCheckbox = (key, val) => {
        let temp_others = [...others];
        let other_index = temp_others.findIndex(o => o.filter_name === key);
        if(other_index === -1) return false;

        let temp_values = [];
        if(temp_others[other_index].value) temp_values = temp_others[other_index].value.split(",");

        let new_value;
        if(temp_values.length === 0 || temp_values.length === 1) new_value = "";
        else new_value = temp_values.filter(t => t !== val).join();

        temp_others[other_index].value = new_value;
        setOthers(temp_others);

        if(!new_value) return "delete";
        return new_value;
    }

    const getFilterBadges = () => {
        let temp_badges = [];
        if(name.trim()) temp_badges.push({key: "name", value: name, removeFunction: removeName});
        if(isFavorit) temp_badges.push({key: "is_favourite", value: "kedvenc", removeFunction: removeFavourite});
        if(+distance) temp_badges.push({key: "distance", value: `${distance} km`, removeFunction: removeDistance});

        if(others && others.length > 0) {
            for(let o in others) {
                if(others[o].value) {
                    if(others[o].type === "radio") temp_badges.push({key: others[o].filter_name, value: getOtherFilterName(others[o].filter_name, others[o].value), removeFunction: () => removeOtherRadio(others[o].filter_name)});
                    else if(others[o].type === "checkbox") {
                        let temp_checkbox_values = others[o].value.split(",");
                        for(let t in temp_checkbox_values) {
                            temp_badges.push({key: others[o].filter_name, value: getOtherFilterName(others[o].filter_name, temp_checkbox_values[t]), removeFunction: () => removeOtherCheckbox(others[o].filter_name, temp_checkbox_values[t])})
                        }
                    }
                }
            }
        }

        return temp_badges;
    }

    useEffect(() => {
        setProviders(props.providers)
    }, [props.providers])
    
    useEffect(() => {
        setPager(props.pager)
    }, [props.pager])

    const context = {
        isFavorit: isFavorit,
        setIsFavorit: (is_favorite) => setIsFavorit(is_favorite),
        name: name,
        setName: (provider_name) => setName(provider_name),
        addressCity: addressCity,
        setAddressCity: (address_city) => setAddressCity(address_city),
        distance: distance,
        setDistance: (provider_distance) => setDistance(provider_distance),
        others: others,
        setOthers: (other_filters) => setOthers(other_filters),
        providers: providers,
        perPage: perPage,
        setPerPage: (per_page) => setPerPage(per_page),
        order: order,
        setOrder: (new_order) => setOrder(new_order),
        pager: pager,
        setPager: (new_pager) => setPager(new_pager),
        showMore: showMore,
        removeFilter: removeFilter,
        getFilterBadges: getFilterBadges
    }

    return <FilterContext.Provider value={context}>
        {props.children}
    </FilterContext.Provider>
}

export default FilterContext;